/**
 * Mobile sticky bottom bar showing order summary toggle + total.
 */
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { formatPrice } from '@/utils/formatting'
import { OrderSummaryPanel } from './OrderSummaryPanel'
import type { CartItem } from '@/types/cart'

interface CheckoutMobileBarProps {
  isOpen: boolean
  onToggle: () => void
  items: CartItem[]
  subtotal: number
  totalItems: number
  taxRate: number | null
  tipAmount: number
  grandTotal: number
  currency: string
}

export function CheckoutMobileBar({
  isOpen,
  onToggle,
  items,
  subtotal,
  totalItems,
  taxRate,
  tipAmount,
  grandTotal,
  currency,
}: CheckoutMobileBarProps) {
  const { t } = useTranslation()

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={onToggle}
        />
      )}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden">
        {isOpen && (
          <div className="bg-card border-t shadow-lg max-h-[50vh] overflow-y-auto px-4 py-4 space-y-4">
            <OrderSummaryPanel
              items={items}
              subtotal={subtotal}
              totalItems={totalItems}
              taxRate={taxRate}
              deliveryFee={null}
              tipAmount={tipAmount}
              orderType="pickup"
              grandTotal={grandTotal}
              currency={currency}
            />
          </div>
        )}
        <button
          onClick={onToggle}
          className="w-full bg-card border-t px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-medium">
              <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              {t('common:orderSummary')} &middot; {t('common:itemWithCount', { count: totalItems })}
            </span>
            <span className="font-bold">{formatPrice(grandTotal, currency)}</span>
          </div>
        </button>
      </div>
    </>
  )
}
